/**
 * axe-core dev-mode runtime a11y scanner (RFC M Phase 3.1b).
 *
 * Loads `@axe-core/react` only when `process.env.NODE_ENV === 'development'`
 * AND we're running in the browser. Findings appear as warnings in the
 * browser DevTools console (grouped, with permalinks to axe rule docs).
 *
 * Why runtime instead of build-time:
 *   - `next lint` is broken in Next 16; flat-config ESLint setup with
 *     `eslint-config-next` hits a circular-ref bug in ESLint 9 (RFC M
 *     Phase 3.1b note). We get the same a11y rule coverage from axe at
 *     runtime, with the bonus that it scans the actual rendered DOM
 *     (catches dynamic state issues a static lint can't).
 *   - Zero impact on prod bundle: dynamic import is gated on NODE_ENV
 *     so the production build tree-shakes it out entirely.
 *
 * Caveats:
 *   - Runs after every React commit. Can be noisy on dashboards with
 *     lots of re-renders; toggleable per developer via localStorage
 *     key `__novixone_a11y_dev` (set to 'off' to silence).
 *   - Some warnings are false positives in dev (Tailwind 4 contrast
 *     vars not yet computed at first paint). Triage manually.
 */
'use client'

import { useEffect } from 'react'

const STORAGE_KEY = '__novixone_a11y_dev'

export function AxeDevReporter() {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return
    if (typeof window === 'undefined') return
    if (window.localStorage.getItem(STORAGE_KEY) === 'off') return

    let cancelled = false
    void (async () => {
      const [React, ReactDOM, axe] = await Promise.all([
        import('react'),
        import('react-dom'),
        import('@axe-core/react'),
      ])
      if (cancelled) return
      // 1000ms debounce — many state updates land within a single user
      // gesture (form input, route nav). Coalescing them keeps console
      // noise down. Pass `null` as context to scan the entire document.
      axe.default(React.default, ReactDOM.default, 1000)
       
      console.info(
        '[a11y] axe-core dev reporter active. Set localStorage.%s = "off" to silence.',
        STORAGE_KEY,
      )
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return null
}
